import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-oneportal";
import type { DeviceStatus } from "@/api/services/manageDevicesServices";
import styles from "./ManageDevicesFilterPanel.module.css";

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={expanded ? styles.chevronExpanded : undefined}
    >
      <path
        d="m4 6 4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface DeviceFilterValues {
  statuses: string[];
  deviceTypes: string[];
  locations: string[];
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface ManageDevicesFilterPanelProps {
  open: boolean;
  /** Currently applied filter values (used to seed the draft when opened). */
  value: DeviceFilterValues;
  /** Device-type options with display labels (e.g. { label: "Security Panel", value: "SecurityPanel" }). */
  deviceTypeOptions: FilterOption[];
  /** Location names fetched from the API. */
  locationOptions: string[];
  /** True while API-backed filter options are still loading. */
  isOptionsLoading?: boolean;
  onApply: (next: DeviceFilterValues) => void;
  onClose: () => void;
}

const STATUS_VALUES: DeviceStatus[] = ["online", "offline"];

const EMPTY: DeviceFilterValues = {
  statuses: [],
  deviceTypes: [],
  locations: [],
};

const toggle = (list: string[], value: string): string[] =>
  list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];

export const ManageDevicesFilterPanel = ({
  open,
  value,
  deviceTypeOptions,
  locationOptions,
  isOptionsLoading = false,
  onApply,
  onClose,
}: ManageDevicesFilterPanelProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DeviceFilterValues>(value);
  const [expandedGroups, setExpandedGroups] = useState<Record<keyof DeviceFilterValues, boolean>>({
    statuses: true,
    deviceTypes: true,
    locations: true,
  });

  const statusOptions: FilterOption[] = STATUS_VALUES.map((v) => ({
    label: t(`manageDevices.statusOptions.${v}`),
    value: v,
  }));

  // Re-seed the draft from the applied values every time the panel opens so
  // that cancelling discards any unsaved edits.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // Close on Escape while the panel is open.
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const renderGroup = (
    title: string,
    options: FilterOption[],
    key: keyof DeviceFilterValues
  ) => {
    const isApiBackedGroup = key !== "statuses";
    const shouldHide = isApiBackedGroup && !isOptionsLoading && options.length === 0;

    if (shouldHide) {
      return null;
    }

    return (
    <fieldset className={styles.group}>
      <legend className={styles.groupLegend}>
        <button
          type="button"
          className={styles.groupToggle}
          aria-expanded={expandedGroups[key]}
          aria-controls={`filter-group-${key}`}
          onClick={() =>
            setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
          }
        >
          <span className={styles.groupTitle}>{title}</span>
          <ChevronIcon expanded={expandedGroups[key]} />
        </button>
      </legend>
      {expandedGroups[key] && (
        <div id={`filter-group-${key}`} className={styles.options}>
          {isOptionsLoading && key !== "statuses" && options.length === 0 ? (
            <p className={styles.emptyOption}>{t("manageDevices.loadingOptions")}</p>
          ) : options.length === 0 ? (
            <p className={styles.emptyOption}>{t("manageDevices.noOptions")}</p>
          ) : (
            options.map((option) => (
              <label key={option.value} className={styles.option}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={draft[key].includes(option.value)}
                  onChange={() =>
                    setDraft((prev) => ({
                      ...prev,
                      [key]: toggle(prev[key], option.value),
                    }))
                  }
                />
                <span className={styles.optionLabel}>{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </fieldset>
    );
  };

  const hasDraftSelections = Object.values(draft).some((group) => group.length > 0);

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={t("manageDevices.ariaLabels.filterDevices")}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.heading}>{t("manageDevices.filterHeading")}</h2>
            <Button
              variant="text"
              className={styles.headerClearButton}
              onClick={() => setDraft(EMPTY)}
              disabled={!hasDraftSelections}
            >
              {t("manageDevices.filterClearAll")}
            </Button>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("manageDevices.ariaLabels.closeFilterPanel")}
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          {renderGroup(t("manageDevices.groups.status"), statusOptions, "statuses")}
          {renderGroup(
            t("manageDevices.groups.deviceType"),
            deviceTypeOptions,
            "deviceTypes"
          )}
          {renderGroup(
            t("manageDevices.groups.location"),
            locationOptions.map((name) => ({ label: name, value: name })),
            "locations"
          )}
        </div>

        <footer className={styles.footer}>
          <Button
            variant="highlightTertiary"
            className={styles.cancelButton}
            onClick={onClose}
          >
            {t("manageDevices.cancel")}
          </Button>
          <Button
            variant="primary"
            className={styles.applyButton}
            onClick={() => onApply(draft)}
          >
            {t("manageDevices.apply")}
          </Button>
        </footer>
      </aside>
    </div>
  );
};

export default ManageDevicesFilterPanel;
